import { throwErrorWithId } from '@pypestream/utils';
import { useState } from 'react';

import { ActionFile } from '../components';
import { tableStore } from '../stores/table-store';
import {
  uploadFile,
  uploadFilesSequentially,
  DataFileType,
  progressServerConfig,
} from '../utils';
import {
  useProStudioCtxSelector,
  sendProStudioEvent,
} from '../xstate/app.xstate';
import { ProStudioContext } from '../xstate/prostudio.xstate.types';

export const useUploadPythonFiles = (
  type: Exclude<DataFileType, 'assetData'>
) => {
  const [uploadingFiles, setUploadingFiles] = useState<ActionFile[]>([]);

  const { versionId, draft, projectId } = useProStudioCtxSelector((ctx) => ({
    versionId:
      ctx.customerId && ctx.botName && ctx.draftVersion
        ? `${ctx.customerId}.${ctx.botName}.${ctx.draftVersion}`
        : undefined,
    draft: ctx.draft,
    projectId: ctx.project?.id,
  }));

  const removePlaceholder = (id: string) => {
    setUploadingFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const uploadOne = (file: File, index: number): Promise<void> => {
    if (!versionId) {
      throwErrorWithId('Version ID is required');
    }

    if (!projectId) {
      throwErrorWithId('Project ID is required');
    }

    // Stable key for this in-flight placeholder, matched on exactly instead of
    // the previous `name.startsWith` (which mismatched shared-prefix names).
    // Uniqueness comes from `index` plus the remove-before-next sequencing
    // below — only one placeholder is ever live at a time (PSTUDIO-3173).
    const id = `${file.name}-${index}-${Date.now()}`;

    setUploadingFiles((prev) => [
      ...prev,
      { id, name: `${file.name} (uploading: 0%)`, path: '', loading: true },
    ]);

    return new Promise<void>((resolve) => {
      uploadFile({
        url: `${import.meta.env.FE_ADMIN_URL}/rest/bot-manager/${projectId}/${versionId}/${type === 'scriptFile' ? 'scripts' : 'config'}`,
        file: [file],
        type,
        onChange: ({ data: versionData }) => {
          sendProStudioEvent({
            type: 'updateDraftData',
            draft: (versionData as ProStudioContext['draft']) || draft,
            action: 'upload',
          });
          removePlaceholder(id);
          resolve();
        },
        error: (error) => {
          console.error(error);
          removePlaceholder(id);
          sendProStudioEvent({
            type: 'showError',
            error,
          });
          resolve();
        },
        progress: (current, loaded, total) => {
          progressServerConfig(current, loaded, total, (p) => {
            setUploadingFiles((prev) =>
              prev.map((uploadingFile) =>
                uploadingFile.id === id
                  ? {
                      ...uploadingFile,
                      name: `${file.name} (uploading: ${p}%)`,
                    }
                  : uploadingFile
              )
            );
          });
        },
      });
    });
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (!files) return;

    await uploadFilesSequentially(Array.from(files), uploadOne);

    await tableStore.validateAndSync();
    tableStore.markDraftFilesChanged();
  };

  return {
    uploadingFiles,
    handleUpload,
  };
};
